import type { BuildingsEditAdapter } from './CompositeGeoEditAdapter';
import type { AddBuildingOp, RemoveBuildingOp } from '@visualizer/types';
import type { BuildingsVisual } from './Interfaces/BuildingsVisual';
import { BuildingSource } from '@visualizer/types';
import * as THREE from 'three';
import { getOnlyGeometryFromRawData } from '../utils';

export class BuildingsGeoEditAdapter implements BuildingsEditAdapter {
    // Removed Buildings
    private removed = new Set<string>();

    // Track what we added so undo/redo works
    private addedKind = new Map<number, 'batched' | 'overlay'>();

    constructor(private readonly visual: BuildingsVisual) {}

    private key(op: RemoveBuildingOp): string {
        return `${op.source}:${op.id}`;
    }

    // Remove operations
    applyRemove(op: RemoveBuildingOp): void {
        const k = this.key(op);
        if (this.removed.has(k)) return;

        this.removed.add(k);
        this.visual.hideBuilding(op.source, op.id);
    }

    revertRemove(op: RemoveBuildingOp): void {
        const k = this.key(op);
        if (!this.removed.has(k)) return;

        this.removed.delete(k);
        this.visual.showBuilding(op.source, op.id);
    }

    applyAdd(op: AddBuildingOp): void {
        if (this.addedKind.has(op.id)) return;

        // add to batched immediately
        if (op.source === BuildingSource.CUSTOM_OBS) {
            const ok = (this.visual as any).addCustomBuildingToBatch?.(op.id, op.groups);
            if (!ok) {
                console.error('[applyAdd] Custom building was NOT added to BatchedMesh (this must be fixed).');
            }
            this.addedKind.set(op.id, 'batched');
            return;
        }

        if (op.source === BuildingSource.SKETCH) {
            const ok = (this.visual as any).addProjectSketchToBatch?.(op.id, op.groups);
            if (!ok) {
                console.error('[applyAdd] Project sketch was NOT added to BatchedMesh (this must be fixed).');
            }
            this.addedKind.set(op.id, 'batched');
            return;
        }

        // Fallback overlay for non-custom
        const root = new THREE.Group();
        root.name = `temp-building-${op.id}`;
        root.userData.tempBuildingId = op.id;
        root.userData.buildingSource = op.source;

        for (const group of op.groups) {
            for (const obj of group.objects) {
                const geometry = getOnlyGeometryFromRawData(obj);
                geometry.computeVertexNormals();
                const mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ side: THREE.DoubleSide }));
                mesh.userData.buildingId = op.id;
                mesh.userData.buildingSource = op.source;
                root.add(mesh);
            }
        }

        this.addedKind.set(op.id, 'overlay');
        this.visual.addTempBuilding(op.id, root);
    }

    revertAdd(op: AddBuildingOp): void {
        const kind = this.addedKind.get(op.id);
        if (!kind) return;

        if (kind === 'batched') {
            // Undo for batched: hide the building id you just added
            this.visual.hideBuilding(op.source, op.id);
        } else {
            this.visual.removeTempBuilding(op.id);
        }

        this.addedKind.delete(op.id);
    }
}
